import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import TierDiscountStrip from "../components/TierDiscountStrip";
import SEO, { LOCAL_BUSINESS_JSONLD } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SareeRentalMelbourne() {
  const [outfits, setOutfits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/outfits?category=Sarees`)
      .then(r => r.json())
      .then(d => setOutfits(d.filter(o => o.available).slice(0, 12)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <SEO
        rawTitle="Saree Rental Melbourne – Hire Designer & Silk Sarees | Mahra"
        description="Rent premium sarees in Melbourne for weddings, festivals and cultural events. Banarasi silks, contemporary drapes and more. Free local pickup from Oakleigh South."
        path="/saree-rental-melbourne"
        jsonLd={LOCAL_BUSINESS_JSONLD}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Saree Rental Melbourne" },
        ]}
      />
      <Navbar />
      <TierDiscountStrip />

      {/* Hero Section */}
      <section className="bg-white border-b border-[#282828]/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-[12px] mb-6" data-testid="seo-breadcrumb">
            <Link to="/" className="text-[#676664]/50 hover:text-[#676664] transition-colors">Home</Link>
            <span className="text-[#676664]/30">/</span>
            <Link to="/indian-outfit-hire-melbourne" className="text-[#676664]/50 hover:text-[#676664] transition-colors">Indian Outfit Hire</Link>
            <span className="text-[#676664]/30">/</span>
            <span className="text-[#282828] font-medium">Saree Rental Melbourne</span>
          </nav>
          <h1 className="text-3xl lg:text-5xl text-[#282828] font-light leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="seo-page-h1">
            Saree Rental <span className="italic">in Melbourne</span>
          </h1>
          <p className="text-[14px] text-[#676664] leading-relaxed max-w-2xl">
            Hire beautiful premium sarees in Melbourne. From timeless Banarasi silks to modern party-wear drapes, rent the perfect saree for weddings, festivals, and cultural celebrations.
          </p>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-12 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10">
          <h2 className="text-xl lg:text-2xl text-[#282828] font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Sarees Available for Rent</h2>
          <p className="text-[13px] text-[#676664] mb-8">Explore our range of Indian outfits available for rent in Melbourne.</p>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {[...Array(8)].map((_, i) => <div key={i} className="bg-[#E8E0D5] animate-pulse" style={{ paddingBottom: '133%' }} />)}
            </div>
          ) : outfits.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="seo-product-grid">
              {outfits.map(outfit => (
                <ProductCard key={outfit.id} outfit={outfit} />
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-[#676664]">Our saree collection is being updated. Please check back soon or <Link to="/catalog" className="text-[#9A7B4F] hover:underline">browse our full collection</Link>.</p>
          )}
          <div className="mt-8 text-center">
            <Link to="/catalog" className="inline-flex items-center gap-2 text-[11px] text-[#282828] tracking-[0.2em] uppercase border-b border-[#282828] pb-1 hover:opacity-70 transition-opacity" data-testid="view-all-sarees-link">
              View All Sarees <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="border-t border-[#282828]/5 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-14 lg:py-20">
          <div className="max-w-3xl">
            <h2 className="text-xl lg:text-2xl text-[#282828] font-light mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Hire a Saree in Melbourne</h2>
            <div className="text-[13px] text-[#676664] leading-relaxed space-y-4">
              <p>The saree is timeless — a six-yard canvas of art, heritage, and personal expression. Whether you're attending a Melbourne wedding, celebrating Diwali, or marking a milestone, hiring a saree from Mahra lets you experience the beauty of designer Indian fabrics without the commitment of ownership.</p>

              <p><strong className="text-[#282828] font-medium">Designer Sarees for Every Occasion</strong></p>
              <p>Our saree collection spans a rich variety of styles and fabrics. From the rich gold weaves of Banarasi silk to the flowing elegance of chiffon and georgette, each saree in our collection has been chosen for its craftsmanship and visual impact. Full-set rentals include a matching stitched blouse, so you're ready to drape and go.</p>

              <p><strong className="text-[#282828] font-medium">Wedding & Festive Sarees</strong></p>
              <p>For weddings, we carry sarees with heavy borders, intricate zari work, and jewel-toned palettes that photograph beautifully. For festivals and lighter occasions, our collection includes contemporary prints, pastel tones, and modern draping styles that suit Melbourne's relaxed yet refined aesthetic.</p>

              <p><strong className="text-[#282828] font-medium">Effortless Rental Experience</strong></p>
              <p>Renting a saree from Mahra is as simple as choosing your favourite, picking your dates, and selecting pickup or delivery. Our 4-day and 8-day rental windows give you flexibility, and every saree arrives freshly dry-cleaned and beautifully packaged. Free local pickup is available from our Oakleigh South location, with delivery across Melbourne for orders over $175.</p>

              <p><strong className="text-[#282828] font-medium">Style It Your Way</strong></p>
              <p>Elevate your saree look by adding complementary pieces through our <Link to="/build-your-look" className="text-[#9A7B4F] hover:underline">Build Your Look</Link> feature. Choose from our range of jewellery sets and clutches, and enjoy bundle discounts of up to 20% when you hire multiple items together.</p>

              <p>Explore our complete range of <Link to="/indian-outfit-hire-melbourne" className="text-[#9A7B4F] hover:underline">premium Indian outfits for hire in Melbourne</Link>, or see how our <Link to="/lehenga-hire-melbourne" className="text-[#9A7B4F] hover:underline">lehengas for hire</Link> can make your next event unforgettable.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="border-t border-[#282828]/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-10">
          <p className="text-[10px] text-[#676664] tracking-[0.3em] uppercase mb-4">Explore More</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/indian-outfit-hire-melbourne" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-indian">Indian Outfit Hire Melbourne</Link>
            <Link to="/lehenga-hire-melbourne" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-lehenga">Lehenga Hire Melbourne</Link>
            <Link to="/looks" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-looks">Curated Looks</Link>
            <Link to="/build-your-look" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-build">Build Your Look</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
